import React, { useState, useEffect } from 'react';
import { Save, Briefcase, Target, Award, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ChipEditor } from './ChipEditor';

export function Capabilities() {
  const { profile, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);

  // NAICS codes and set-asides are owned by Company Information (the canonical edit
  // point) — read-only here, used only for the score/badges.
  const naicsCodes: string[] = profile?.naics_codes || [];
  const qualifiedSetAsides: string[] = profile?.qualified_set_asides || [];

  const [formData, setFormData] = useState({
    coreCompetencies: profile?.core_competencies || [],
    industries: profile?.industries_served || [],
    technologies: profile?.key_technologies || [],
    valueProposition: profile?.value_proposition || '',
    differentiators: profile?.differentiators || [],
    certifications: profile?.certifications || [],
    specializations: profile?.specializations || [],
    marketFocus: profile?.market_focus || [],
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        coreCompetencies: profile.core_competencies || [],
        industries: profile.industries_served || [],
        technologies: profile.key_technologies || [],
        valueProposition: profile.value_proposition || '',
        differentiators: profile.differentiators || [],
        certifications: profile.certifications || [],
        specializations: profile.specializations || [],
        marketFocus: profile.market_focus || [],
      });
    }
  }, [profile]);

  const set = (field: keyof typeof formData, val: string[] | string) =>
    setFormData((prev) => ({ ...prev, [field]: val }));

  // Capability completeness score (also stored as value_proposition_score).
  const calculateCapabilityScore = () => {
    const w = { core: 25, tech: 20, value: 15, naics: 15, setAside: 10, certs: 10, diff: 5 };
    let score = 0;
    score += Math.min(formData.coreCompetencies.filter(Boolean).length / 3, 1) * w.core;
    score += Math.min(formData.technologies.filter(Boolean).length / 5, 1) * w.tech;
    score += Math.min(formData.valueProposition.length / 100, 1) * w.value;
    score += Math.min(naicsCodes.length / 3, 1) * w.naics;
    if (qualifiedSetAsides.length >= 1) score += w.setAside;
    score += Math.min(formData.certifications.filter(Boolean).length / 2, 1) * w.certs;
    score += Math.min(formData.differentiators.filter(Boolean).length / 2, 1) * w.diff;
    return Math.round(Math.min(score, 100));
  };
  const capabilityScore = calculateCapabilityScore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const clean = (a: string[]) => (a || []).map((s) => s.trim()).filter(Boolean);
      await updateProfile({
        core_competencies: clean(formData.coreCompetencies),
        industries_served: clean(formData.industries),
        key_technologies: clean(formData.technologies),
        value_proposition: formData.valueProposition,
        differentiators: clean(formData.differentiators),
        certifications: clean(formData.certifications),
        specializations: clean(formData.specializations),
        market_focus: clean(formData.marketFocus),
        value_proposition_score: capabilityScore,
        // NAICS / set-asides intentionally NOT written here — Company Information owns them.
      });
      toast.success('Capabilities updated');
    } catch (error) {
      console.error('Error updating capabilities:', error);
      toast.error('Failed to update capabilities');
    } finally {
      setLoading(false);
    }
  };

  const metric = (label: string, count: number, target: string) => (
    <div className="bg-bw-surface rounded-lg p-4 border border-bw-border">
      <div className="flex items-center justify-between">
        <span className="text-sm text-bw-muted">{label}</span>
        <span className="text-lg font-bold text-bw-primary">{count}</span>
      </div>
      <div className="text-xs text-bw-muted mt-1">{target}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Intelligence dashboard */}
      <div className="bg-bw-surface rounded-xl p-6 border border-bw-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-bw-ink flex items-center">
            <Target className="h-5 w-5 mr-2 text-bw-primary" />
            Capability Intelligence
          </h3>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            capabilityScore >= 80 ? 'text-success bg-success-bg'
              : capabilityScore >= 60 ? 'text-fit-moderate bg-fit-moderate-bg' : 'text-urgent bg-urgent-bg'
          }`}>
            {capabilityScore}% Complete
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-bw-surface rounded-lg p-4 border border-bw-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Capability Score</span>
              <span className="text-lg font-bold text-bw-ink">{capabilityScore}%</span>
            </div>
            <div className="w-full bg-fit-track rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  capabilityScore >= 80 ? 'bg-fit-strong-stroke' : capabilityScore >= 60 ? 'bg-fit-moderate-stroke' : 'bg-fit-weak-stroke'
                }`}
                style={{ width: `${capabilityScore}%` }}
              />
            </div>
          </div>
          {metric('Core Competencies', formData.coreCompetencies.filter(Boolean).length, 'Recommended: 3+')}
          {metric('Technologies', formData.technologies.filter(Boolean).length, 'Recommended: 5+')}
          {metric('NAICS Codes', naicsCodes.filter(Boolean).length, 'Edit in Company Information')}
        </div>
      </div>

      <div className="bg-bw-primary-tint/40 border border-bw-border rounded-lg p-3 flex items-start gap-2">
        <Info className="h-4 w-4 text-bw-primary mt-0.5 shrink-0" />
        <p className="text-xs text-bw-ink">
          Everything here feeds proposal generation — your core competencies, technologies, specializations, certifications,
          and differentiators are quoted directly when BidWin writes the Technical, Management, and Executive sections.
          If a capability isn't listed, it won't be claimed.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-bw-surface rounded-xl border border-bw-border p-6 space-y-6">
        <h3 className="text-lg font-semibold flex items-center">
          <Briefcase className="h-5 w-5 mr-2 text-bw-muted" />
          Capabilities &amp; Core Services
        </h3>

        <ChipEditor
          label="Core Competencies"
          value={formData.coreCompetencies}
          onChange={(v) => set('coreCompetencies', v)}
          placeholder="e.g. Software Development, Cybersecurity, Cloud Engineering"
          recommend={3}
          helpText="The headline things you do. These become the backbone of the technical approach."
        />

        <ChipEditor
          label="Key Technologies"
          value={formData.technologies}
          onChange={(v) => set('technologies', v)}
          placeholder="e.g. React, Python, AWS, Kubernetes, Section 508"
          recommend={5}
          suggestions={['AWS', 'Azure', 'React', 'Python', 'Kubernetes', 'PostgreSQL', 'Section 508', 'FedRAMP']}
        />

        <ChipEditor
          label="Industries Served"
          value={formData.industries}
          onChange={(v) => set('industries', v)}
          placeholder="e.g. Defense, Healthcare, Civilian Agencies"
          suggestions={['Department of Defense', 'Veterans Affairs', 'Health & Human Services', 'Civilian', 'Intelligence']}
        />

        <ChipEditor
          label="Specializations"
          value={formData.specializations}
          onChange={(v) => set('specializations', v)}
          placeholder="e.g. Zero Trust Architecture, ATO/RMF, Instructional Design"
          helpText="Niche strengths that distinguish you within your competencies."
        />

        <ChipEditor
          label="Market Focus"
          value={formData.marketFocus}
          onChange={(v) => set('marketFocus', v)}
          placeholder="e.g. SDVOSB set-asides, IDIQ task orders, sub-$10M services"
          helpText="The kinds of opportunities you target — used to frame win themes."
        />

        <div>
          <label className="block text-sm font-medium text-bw-ink mb-1.5 flex items-center">
            <Award className="h-4 w-4 mr-1" /> Certifications &amp; Qualifications
          </label>
          <ChipEditor
            value={formData.certifications}
            onChange={(v) => set('certifications', v)}
            placeholder="e.g. ISO 9001, CMMI Level 3, AWS Certified"
            recommend={2}
            suggestions={['ISO 9001', 'ISO 27001', 'CMMI Level 3', 'SOC 2', 'AWS Certified']}
          />
        </div>

        <ChipEditor
          label="Competitive Differentiators"
          value={formData.differentiators}
          onChange={(v) => set('differentiators', v)}
          placeholder="e.g. 24/7 CONUS support, Active TS/SCI staff, Agile delivery"
          recommend={2}
          helpText="What sets you apart from other bidders — these become win themes."
        />

        <div>
          <label className="block text-sm font-medium text-bw-ink mb-1.5">Value Proposition</label>
          <textarea
            value={formData.valueProposition}
            onChange={(e) => set('valueProposition', e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
            placeholder="Describe what makes your company uniquely able to deliver this kind of work…"
          />
          <div className="text-xs text-bw-muted mt-1">{formData.valueProposition.length}/100+ characters recommended</div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:brightness-110 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving…' : 'Save Capabilities'}
          </button>
        </div>
      </form>
    </div>
  );
}
